import classes from 'components/molecules/SurveyCard/SurveyIcon.qmee.module.scss';
import dynamic from 'next/dynamic';
import React, { ComponentType, SVGProps, useMemo } from 'react';
import FallbackIcon from './assets/qmee/fallback.svg';
import StreakOverlay from './assets/qmee/streak-overlay.svg';

export type Props = {
  surveyTags: ReadonlyArray<string>;
  surveyId: string;
  streakActive?: boolean;
  surveyTitle?: string;
};

const ICON_MAP = {
  general_topics: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/general_topics.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  market_topics: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/market_topics.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  consumer_attitudes: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/consumer_attitudes.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  consumer_trends: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/consumer_trends.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  customer_opinions: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/customer_opinions.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  drinks_diary: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/drinks_diary.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  family_insights: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/family_insights.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  global_topics: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/global_topics.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  global_trends: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/global_trends.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  market_trends: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/market_trends.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  my_personal_routine: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/my_personal_routine.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  my_telco: dynamic(() => import('components/molecules/SurveyCard/assets/qmee/my_telco.svg'), {
    loading: () => <FallbackIcon className={classes.icon} />,
    ssr: false,
  }),
  product_test: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/product_test.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  product_test_reconnect: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/product_test_reconnect.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  purchase_diary: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/purchase_diary.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  quick_poll: dynamic(() => import('components/molecules/SurveyCard/assets/qmee/quick_poll.svg'), {
    loading: () => <FallbackIcon className={classes.icon} />,
    ssr: false,
  }),
  recall: dynamic(() => import('components/molecules/SurveyCard/assets/qmee/recall.svg'), {
    loading: () => <FallbackIcon className={classes.icon} />,
    ssr: false,
  }),
  trending_topics: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/trending_topics.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
  diary_study: dynamic(
    () => import('components/molecules/SurveyCard/assets/qmee/diary_study.svg'),
    {
      loading: () => <FallbackIcon className={classes.icon} />,
      ssr: false,
    },
  ),
} satisfies Record<string, ComponentType<SVGProps<SVGSVGElement>>>;

type IconKey = keyof typeof ICON_MAP;

type SurveyTitleConfig = {
  icon: IconKey;
};

const COLORS = ['#FF495C', '#2BD675', '#AA2BB6', '#338BFF', '#F5CC00', '#FF37A6'];

const SURVEY_TITLE_MAP: Record<string, SurveyTitleConfig> = {
  'emerging-topics': { icon: 'trending_topics' },
  'global-trends': { icon: 'global_trends' },
  'industry-topics': { icon: 'market_topics' },
  'popular-trends': { icon: 'market_trends' },
  'key-topics': { icon: 'recall' },
  'current-trends': { icon: 'consumer_trends' },
  'hot-topics': { icon: 'trending_topics' },
  'global-topics': { icon: 'global_topics' },
  'niche-trends': { icon: 'customer_opinions' },
  'broad-topics': { icon: 'family_insights' },
  'future-trends': { icon: 'global_trends' },
  'ongoing-topics': { icon: 'general_topics' },
  'cultural-trends': { icon: 'consumer_attitudes' },
  'major-topics': { icon: 'general_topics' },
  'business-trends': { icon: 'purchase_diary' },
  'contemporary-topics': { icon: 'customer_opinions' },
  'lifestyle-trends': { icon: 'my_personal_routine' },
  'core-topics': { icon: 'recall' },
  'relevant-trends': { icon: 'drinks_diary' },
  'timeless-topics': { icon: 'general_topics' },
};

function getRandomColor(): string {
  return COLORS[Math.floor(Math.random() * COLORS.length)];
}

function toKebabCase(value?: string): string | undefined {
  return value
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getSurveyPresentation(
  surveyTags: ReadonlyArray<string>,
  surveyTitle?: string,
): { color: string; icon: IconKey } {
  const kebabSurveyTitle = toKebabCase(surveyTitle);
  const titleConfig = kebabSurveyTitle ? SURVEY_TITLE_MAP[kebabSurveyTitle] : undefined;

  if (titleConfig) {
    return {
      color: getRandomColor(),
      icon: titleConfig.icon,
    };
  }

  const surveyTag = surveyTags[0] as IconKey | undefined;
  const fallbackIcon = surveyTag && surveyTag in ICON_MAP ? surveyTag : 'general_topics';

  return {
    icon: fallbackIcon,
    color: getRandomColor(),
  };
}

function SurveyIcon({ surveyTags, streakActive, surveyTitle }: Props) {
  const { color, icon } = useMemo(
    () => getSurveyPresentation(surveyTags, surveyTitle),
    [surveyTags, surveyTitle],
  );
  const DynamicIcon = ICON_MAP[icon];

  return (
    <i className={classes.root}>
      {streakActive && (
        <StreakOverlay
          style={{
            color,
          }}
          className={classes.streakOverlay}
        />
      )}
      <DynamicIcon
        style={{
          color,
        }}
        className={classes.icon}
      />
    </i>
  );
}

export default React.memo(SurveyIcon);
